import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Image,
	makeClassName,
	Paragraph,
	Row,
	Rule,
	sleep,
	StyleProps,
	toGrammaticalNumber,
	TruncatedText,
	useCount,
	useHasBeenTrue,
} from "@hex-insights/core";
import {
	CheckBoxInput,
	EditMode,
	FormSubmitFunction,
	Label,
	submissionSuccess,
	SubmitButton,
	useFieldState,
	useFormState,
	useInternalField,
	useOnSuccess,
} from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import {
	Icon,
	ParentPortalViewerInfoQuery,
	PersonFormat,
	PersonFormValues,
	PersonPublicNameFormat,
	PersonUpdateInput,
	StaticURLs,
	StudentDataConsentFormConversion,
	StudentDataConsentFormValues,
	StudentDataConsentStudentForm,
	Tile,
	useGuardianOnboardingSubmissionMutation,
	useParentPortalViewerInfoQuery,
	UserComponents,
	UserExternalNotificationFrequency,
	UserFormValues,
	UserUpdateInput,
	useStudentDataConsentListQuery,
} from "@hex-insights/verita-romania.shared";
import { ClassPlacementBody, DataPrivacyStatement, Main } from "../../Components";
import { onboardingPageInfo } from "./pageinfo";
import styles from "./styles.module.css";

type OnboardingUserPreferencesFormValues = {
	publicNameFormat: PersonFormValues.Detail["publicNameFormat"];
	externalNotificationFrequency: UserFormValues.Detail["externalNotificationFrequency"];
};

const initialOnboardingUserPreferencesFormValues: OnboardingUserPreferencesFormValues = {
	publicNameFormat: null,
	externalNotificationFrequency: null,
};

export function OnboardingPage() {
	useActivePageRegistration(onboardingPageInfo);

	const { count: pageIndex, increment: incrementPageIndex, decrement: decrementPageIndex } = useCount();

	const [dataConsentFormValues, setDataConsentFormValues] = React.useState<StudentDataConsentFormValues.Create>(() => ({
		...StudentDataConsentFormValues.initialCreate,
		studentID: "0", // ignored by server
	}));
	const [preferencesFormValues, setPreferencesFormValues] = React.useState(initialOnboardingUserPreferencesFormValues);

	const { loading: loadingViewer, data: viewerData } = useParentPortalViewerInfoQuery();
	const viewer = viewerData?.viewer;

	React.useEffect(() => {
		if (!viewer) {
			return;
		}
		const viewerPerson = viewer.person;
		if (viewerPerson) {
			setPreferencesFormValues((prev) => ({
				...prev,
				publicNameFormat: viewerPerson.publicNameFormat,
				externalNotificationFrequency:
					viewer.externalNotificationFrequency !== UserExternalNotificationFrequency.Never
						? viewer.externalNotificationFrequency
						: null,
			}));
		}
	}, [viewer]);

	const { loading: loadingDataConsent, data: dataConsentData } = useStudentDataConsentListQuery({
		variables: {
			filters: {
				student: [
					{
						relationships: [
							{
								guardian: [
									{
										person: [
											{
												user: [
													{
														isCurrentEQ: true,
													},
												],
											},
										],
									},
								],
							},
						],
					},
				],
			},
		},
	});

	React.useEffect(() => {
		if (!dataConsentData || dataConsentData.studentDataConsentConnection.edges.length === 0) {
			return;
		}

		const formValues: StudentDataConsentFormValues.Create = {
			allowPublishVisualMediaOfStudentGeneral: true,
			allowPublishVisualMediaOfStudentForPromotion: true,
			allowPublishVisualMediaOfGuardianForPromotion: true,
			allowPublishStudentWorkGeneral: true,
			allowShareInformationWithPearsonEdexcel: true,
			allowShareInformationWithIbOrganization: true,
			allowShareInformationWithOnlinePlatforms: true,
			allowBirthdayInCalendar: true,
			allowSendNewsletters: true,
			allowSendCalendarEvents: true,
			studentID: "0",
		};
		for (let i = 0; i < dataConsentData.studentDataConsentConnection.edges.length; i++) {
			const dataConsent = dataConsentData.studentDataConsentConnection.edges[i].node;

			formValues.allowPublishVisualMediaOfStudentGeneral &&= dataConsent.allowPublishVisualMediaOfStudentGeneral;
			formValues.allowPublishVisualMediaOfStudentForPromotion &&=
				dataConsent.allowPublishVisualMediaOfStudentForPromotion;
			formValues.allowPublishVisualMediaOfGuardianForPromotion &&=
				dataConsent.allowPublishVisualMediaOfGuardianForPromotion;
			formValues.allowPublishStudentWorkGeneral &&= dataConsent.allowPublishStudentWorkGeneral;
			formValues.allowShareInformationWithPearsonEdexcel &&= dataConsent.allowShareInformationWithPearsonEdexcel;
			formValues.allowShareInformationWithIbOrganization &&= dataConsent.allowShareInformationWithIbOrganization;
			formValues.allowShareInformationWithOnlinePlatforms &&= dataConsent.allowShareInformationWithOnlinePlatforms;
			formValues.allowBirthdayInCalendar &&= dataConsent.allowBirthdayInCalendar;
			formValues.allowSendNewsletters &&= dataConsent.allowSendNewsletters;
			formValues.allowSendCalendarEvents &&= dataConsent.allowSendCalendarEvents;
		}

		setDataConsentFormValues(formValues);
	}, [dataConsentData]);

	const onSubmitDataConsent = React.useCallback<FormSubmitFunction<StudentDataConsentFormValues.Create>>(
		async (formState) => {
			setDataConsentFormValues(formState.formValues);
			return submissionSuccess();
		},
		[],
	);

	const onSubmitPreferences = React.useCallback<FormSubmitFunction<OnboardingUserPreferencesFormValues>>(
		async (formState) => {
			setPreferencesFormValues(formState.formValues);
			return submissionSuccess();
		},
		[],
	);

	const hasVisitedDataConsent = useHasBeenTrue(pageIndex === 1);

	return (
		<Main className={styles["main"]}>
			<Column justify="center" align="center" className={styles["main-column"]}>
				{viewer && (
					<Conditional>
						<If condition={pageIndex === 0}>
							<WelcomeTile
								onNextClick={incrementPageIndex}
								onBackClick={decrementPageIndex}
								isNextDisabled={loadingViewer || loadingDataConsent}
							/>
						</If>
						<If condition={pageIndex === 1}>
							<DataConsentTile
								initialFormValues={dataConsentFormValues}
								onSubmit={onSubmitDataConsent}
								onBackClick={decrementPageIndex}
								onSuccess={incrementPageIndex}
								hasPreviousDataConsent={
									!!dataConsentData && dataConsentData.studentDataConsentConnection.edges.length > 0
								}
								hasVisited={hasVisitedDataConsent}
							/>
						</If>
						<If condition={pageIndex === 2}>
							<PreferencesTile
								viewer={viewer}
								initialFormValues={preferencesFormValues}
								onSubmit={onSubmitPreferences}
								onBackClick={decrementPageIndex}
								onSuccess={incrementPageIndex}
							/>
						</If>
						<If condition={pageIndex === 3}>
							<SubmissionTile
								dataConsentFormValues={dataConsentFormValues}
								preferencesFormValues={preferencesFormValues}
								onBackClick={decrementPageIndex}
								onNextClick={incrementPageIndex}
							/>
						</If>
						<Else>
							<ClassPlacementTile />
						</Else>
					</Conditional>
				)}
			</Column>
		</Main>
	);
}

type OnboardingTileProps = {
	onNextClick: () => void;
	onBackClick: () => void;
} & Partial<StyleProps>;

type WelcomeTileProps = OnboardingTileProps & {
	isNextDisabled?: boolean;
};

function WelcomeTile({ onNextClick, isNextDisabled = false, style }: WelcomeTileProps) {
	return (
		<Tile className={styles["onboarding-tile"]} style={style}>
			<Tile.Body
				className={styles["onboarding-tile__body"]}
				style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
			>
				<Column justify="spaced-start" align="center" verticalSpacing="1.5rem">
					<Column justify="spaced-start" align="center">
						<Row justify="spaced-center" align="center">
							<Image
								src={StaticURLs.Images.studentBoy1}
								sizing="contain"
								style={{ width: "10rem", height: "15.625rem" }}
							/>
							<Image
								src={StaticURLs.Images.studentGirl1}
								sizing="contain"
								style={{ width: "10rem", height: "15.625rem" }}
							/>
						</Row>

						<Heading.H1 noMargin style={{ textAlign: "center" }}>
							Welcome to the Verita Parent&nbsp;Portal!
						</Heading.H1>
						<Paragraph style={{ textAlign: "center", maxWidth: "32rem" }}>
							We're thrilled to have you as part of our community. This portal serves as your hub for real-time updates
							on your child's academic progress, school announcements, and upcoming events.
						</Paragraph>
					</Column>

					<Button variant="primary" onClick={onNextClick} disabled={isNextDisabled} className={styles["button"]}>
						Let's Get Started!
					</Button>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

const dataConsentPresetOptionsGridResponsiveColumns = {
	0: 1,
	500: 3,
};

type DataConsentTileProps = Omit<OnboardingTileProps, "onNextClick"> & {
	initialFormValues: StudentDataConsentFormValues.Create;
	onSubmit: FormSubmitFunction<StudentDataConsentFormValues.Create>;
	onSuccess: () => void;
	hasVisited: boolean;
	hasPreviousDataConsent: boolean;
};

function DataConsentTile({
	initialFormValues,
	onSubmit,
	onBackClick,
	onSuccess,
	hasVisited,
	hasPreviousDataConsent,
	style,
}: DataConsentTileProps) {
	const formState = useFormState({ initialFormValues, editMode: EditMode.WriteOnly });

	useInternalField(formState, "studentID");

	const { selectedPreset, onSelectPreset } = StudentDataConsentStudentForm.useQuestionsPreset({
		formState,
		initiallyBlank: !(hasVisited || hasPreviousDataConsent),
	});

	useOnSuccess(formState, () => onSuccess());

	return (
		<Tile className={styles["onboarding-tile"]} style={style}>
			<Tile.Header className={styles["onboarding-tile__header"]}>
				<Heading.H2 noMargin>Onboarding</Heading.H2>
			</Tile.Header>
			<Tile.Body className={styles["onboarding-tile__body"]}>
				<Column justify="spaced-start" verticalSpacing="1.5rem">
					<DataPrivacyStatement />

					<Rule thickness="2px" />

					<StudentDataConsentStudentForm.QuestionsPresetField
						selectedPreset={selectedPreset}
						onSelectPreset={onSelectPreset}
					/>

					<StudentDataConsentStudentForm.QuestionFields formState={formState} hidden={selectedPreset !== "custom"} />
				</Column>
			</Tile.Body>
			<Tile.Footer className={styles["onboarding-tile__footer"]}>
				<Row justify="space-between">
					<Button
						variant="tertiary"
						disabled={formState.disabled}
						onClick={onBackClick}
						className={styles["button"] + " button--primary-tertiary"}
					>
						Back
					</Button>
					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						className={styles["button"] + " button--primary-tertiary"}
					>
						Continue
					</SubmitButton>
				</Row>
			</Tile.Footer>
		</Tile>
	);
}

type PreferencesTileProps = Omit<OnboardingTileProps, "onNextClick"> & {
	viewer: ParentPortalViewerInfoQuery["viewer"];
	initialFormValues: OnboardingUserPreferencesFormValues;
	onSubmit: FormSubmitFunction<OnboardingUserPreferencesFormValues>;
	onSuccess: () => void;
};

function PreferencesTile({ viewer, initialFormValues, onSubmit, onBackClick, onSuccess, style }: PreferencesTileProps) {
	const formState = useFormState({ initialFormValues, editMode: EditMode.WriteOnly });

	const publicNameFormatFieldState = useFieldState<OnboardingUserPreferencesFormValues, PersonPublicNameFormat | null>({
		formState,
		name: "publicNameFormat",
	});
	const externalNotificationFrequencyFieldState = useFieldState<
		OnboardingUserPreferencesFormValues,
		UserFormValues.Detail["externalNotificationFrequency"]
	>({
		formState,
		name: "externalNotificationFrequency",
	});

	useOnSuccess(formState, () => onSuccess());

	const previewPerson = {
		givenName: viewer.person?.givenName ?? "",
		familyName: viewer.person?.familyName ?? "",
		nameOrder: viewer.person?.nameOrder ?? null,
	};

	const childText = toGrammaticalNumber("child", viewer.person?.guardian?.relationships.length ?? 0);

	return (
		<Tile className={styles["onboarding-tile"]} style={style}>
			<Tile.Header className={styles["onboarding-tile__header"]}>
				<Heading.H2 noMargin>Onboarding</Heading.H2>
			</Tile.Header>
			<Tile.Body className={styles["onboarding-tile__body"]}>
				<Column justify="spaced-start" verticalSpacing="1.5rem">
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							<span className={styles["bold-heading-text"]}>
								How would you like your name to display to others in the Parent Portal?
							</span>

							<span className={styles["secondary-text"]}>
								Your profile will be displayed to relevant admin staff, your {childText}'s teacher(s) and to parents
								within your {childText}'s classroom.
							</span>
						</Column>

						<Grid tracks="auto-fill" minTrackWidth="12.5rem" gap="0.75rem">
							<Button
								onClick={() => publicNameFormatFieldState.setValue(PersonPublicNameFormat.FullName)}
								className={makeClassName(
									styles["public-name-format-button"],
									publicNameFormatFieldState.value === PersonPublicNameFormat.FullName
										? styles["public-name-format-button--selected"]
										: "",
								)}
							>
								<Row justify="spaced-start" align="center">
									<UserComponents.UserIcon user={viewer} size={32} />
									<TruncatedText>
										{PersonFormat.publicNameFromFields({
											...previewPerson,
											publicNameFormat: PersonPublicNameFormat.FullName,
										})}
									</TruncatedText>
								</Row>
							</Button>
							<Button
								onClick={() => publicNameFormatFieldState.setValue(PersonPublicNameFormat.GivenNameFamilyInitial)}
								className={makeClassName(
									styles["public-name-format-button"],
									publicNameFormatFieldState.value === PersonPublicNameFormat.GivenNameFamilyInitial
										? styles["public-name-format-button--selected"]
										: "",
								)}
							>
								<Row justify="spaced-start" align="center">
									<UserComponents.UserIcon user={viewer} size={32} />
									<span>
										{PersonFormat.publicNameFromFields({
											...previewPerson,
											publicNameFormat: PersonPublicNameFormat.GivenNameFamilyInitial,
										})}
									</span>
								</Row>
							</Button>
						</Grid>

						<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
							<CheckBoxInput
								id="public-name-format-hidden"
								value={publicNameFormatFieldState.value === PersonPublicNameFormat.Hidden}
								setValue={(v) =>
									publicNameFormatFieldState.setValue(
										v ? PersonPublicNameFormat.Hidden : PersonPublicNameFormat.FullName,
									)
								}
								style={{ marginTop: "0.125rem" }}
							/>

							<Label htmlFor="public-name-format-hidden">I do not want my name displayed in the Parent Portal.</Label>
						</Row>

						<If condition={formState.formValues.publicNameFormat === PersonPublicNameFormat.Hidden}>
							<Row justify="spaced-start" align="center" className={styles["warning-notice"]}>
								<Icon.WarningOctagon color="var(--theme-colors__pink)" size={20} />
								Please note that by choosing to hide your name in the Parent Portal you will not be able to comment on
								posts, respond in group messages, and may be limited in other ways.
							</Row>
						</If>
					</Column>

					<Rule thickness="2px" />

					<Column justify="spaced-start" verticalSpacing="1rem">
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							<span className={styles["bold-heading-text"]}>
								How often would you like to receive email notifications from the Parent Portal?
							</span>

							<span className={styles["secondary-text"]}>
								We'll only email you if there's something important to share.
							</span>
						</Column>

						<Grid responsiveTracks={dataConsentPresetOptionsGridResponsiveColumns} gap="0.75rem">
							<Button
								variant={
									externalNotificationFrequencyFieldState.value === UserExternalNotificationFrequency.Daily
										? "primary"
										: "tertiary"
								}
								onClick={() =>
									externalNotificationFrequencyFieldState.setValue(UserExternalNotificationFrequency.Daily)
								}
								className={styles["button"] + " button--primary-tertiary"}
								style={{ width: "100%" }}
							>
								Once a Day
							</Button>
							<Button
								variant={
									externalNotificationFrequencyFieldState.value === UserExternalNotificationFrequency.Weekly
										? "primary"
										: "tertiary"
								}
								onClick={() =>
									externalNotificationFrequencyFieldState.setValue(UserExternalNotificationFrequency.Weekly)
								}
								className={styles["button"] + " button--primary-tertiary"}
								style={{ width: "100%" }}
							>
								Once a Week
							</Button>
							<Button
								variant={
									externalNotificationFrequencyFieldState.value === UserExternalNotificationFrequency.Never
										? "primary"
										: "tertiary"
								}
								onClick={() =>
									externalNotificationFrequencyFieldState.setValue(UserExternalNotificationFrequency.Never)
								}
								className={styles["button"] + " button--primary-tertiary"}
								style={{ width: "100%" }}
							>
								When Necessary
							</Button>
						</Grid>

						<span className={styles["secondary-text"]}>
							Based on your choice here we will aggregate the messages so that we avoid sending you too many
							notifications. Please note that urgent messages will be sent separately.
						</span>
					</Column>
				</Column>
			</Tile.Body>
			<Tile.Footer className={styles["onboarding-tile__footer"]}>
				<Row justify="space-between">
					<Button
						variant="tertiary"
						disabled={formState.disabled}
						onClick={onBackClick}
						className={styles["button"] + " button--primary-tertiary"}
					>
						Back
					</Button>
					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						className={styles["button"] + " button--primary-tertiary"}
					>
						Save and Continue
					</SubmitButton>
				</Row>
			</Tile.Footer>
		</Tile>
	);
}

type SubmissionTileProps = OnboardingTileProps & {
	dataConsentFormValues: StudentDataConsentFormValues.Create;
	preferencesFormValues: OnboardingUserPreferencesFormValues;
};

function SubmissionTile({ dataConsentFormValues, preferencesFormValues, onNextClick, style }: SubmissionTileProps) {
	const hasStartedRef = React.useRef(false);
	const [submit] = useGuardianOnboardingSubmissionMutation();
	React.useEffect(() => {
		if (hasStartedRef.current) {
			return;
		}

		(async () => {
			hasStartedRef.current = true;
			const studentDataConsentInput = StudentDataConsentFormConversion.toGQLCreateInput(dataConsentFormValues);
			const personInput: PersonUpdateInput = {
				publicNameFormat: preferencesFormValues.publicNameFormat,
			};
			const userInput: UserUpdateInput = {
				externalNotificationFrequency: preferencesFormValues.externalNotificationFrequency,
			};

			const { errors } = await submit({
				variables: { studentDataConsentInput, personInput, userInput },
				update: (cache) => {
					cache.evict({ id: "ROOT_QUERY", fieldName: "viewer" });
				},
			});

			await sleep(1500);

			if (errors) {
				console.error("errors: ", errors);
			}

			onNextClick();
		})();
	}, [dataConsentFormValues, preferencesFormValues, submit, onNextClick]);

	return (
		<Tile className={styles["onboarding-tile"]} style={style}>
			<Tile.Body className={styles["onboarding-tile__body"]} style={{ display: "flex", flexDirection: "column" }}>
				<Column justify="center" align="center" style={{ flexGrow: 1 }}>
					<Image src={StaticURLs.Images.studentBoy1} sizing="contain" style={{ width: "10rem", height: "15.625rem" }} />

					<span style={{ fontWeight: "bold" }}>Setting up your Parent Portal...</span>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

type ClassPlacementTileProps = Partial<StyleProps>;

function ClassPlacementTile({ style }: ClassPlacementTileProps) {
	const { replace: replaceHistory } = useHistory();

	return (
		<Tile className={styles["onboarding-tile"]} style={style}>
			<Tile.Header className={styles["onboarding-tile__header"]}>
				<Heading.H2 noMargin>Class Placement</Heading.H2>
			</Tile.Header>
			<Tile.Body className={styles["onboarding-tile__body"]}>
				<ClassPlacementBody />
			</Tile.Body>
			<Tile.Footer className={styles["onboarding-tile__footer"]}>
				<Row justify="flex-end">
					<Button variant="primary" onClick={() => replaceHistory("/")}>
						All Done!
					</Button>
				</Row>
			</Tile.Footer>
		</Tile>
	);
}
